import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faLaptop,
  faGraduationCap,
  faBrain,
  faShieldAlt,
  faPaintBrush,
  faUsers,
  faLightbulb,
  faCogs,
  faGlobeAsia,
  faLanguage,
  faChartLine,
  faList,
} from "@fortawesome/free-solid-svg-icons";
import {
  GraduationCap,
  ArrowRight,
  Code,
  Layers,
  Sparkles,
  CircleHelp,
  FilePenLine,
} from "lucide-react";

import SubjectFilter from "./SubjectFilter";
import SubjectFormModal from "./SubjectFormModal";
import SubjectDetailModal from "./SubjectDetailModal";

import {
  getAllSubjects,
  deleteSubject,
  toggleSubjectStatus,
} from "../../../api/elearning/subjects/subjectInfo";

import {
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";
import { startSubjectsPageTour } from "./subjectsPageTour";
import { startSubjectFormTour } from "./subjectFormTour";

const ICON_MAP = {
  book: faBook,
  laptop: faLaptop,
  graduation: faGraduationCap,
  brain: faBrain,
  shield: faShieldAlt,
  paint: faPaintBrush,
  users: faUsers,
  idea: faLightbulb,
  tech: faCogs,
  global: faGlobeAsia,
  language: faLanguage,
  analysis: faChartLine,
};

const SubjectCardPage = () => {
  const [subjects, setSubjects] = useState([]);
  const [filters, setFilters] = useState({
    keyword: "",
    status: "",
  });

  const [showForm, setShowForm] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [queuedTour, setQueuedTour] = useState(null);
  const canQuickTour = hasPermission("elearning.subjects", "R");

  // ===================== LOAD DATA =====================
  const fetchSubjects = async () => {
    try {
      const res = await getAllSubjects(filters);
      setSubjects(res.items || res || []);
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể tải danh sách môn học");
      setSubjects([]);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, [filters]);

  useEffect(() => {
    if (queuedTour === "form" && showForm) {
      window.setTimeout(() => {
        startSubjectFormTour();
        setQueuedTour(null);
      }, 220);
    }
  }, [queuedTour, showForm]);

  // ===================== HANDLERS =====================
  const handleCreate = () => {
    setEditingSubject(null);
    setShowForm(true);
  };

  const openGuide = () => {
    const guideUrl = `${window.location.origin}/guides/elearning-subjects-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  };

  const handlePageTour = () => {
    window.setTimeout(() => startSubjectsPageTour(), 80);
  };

  const handleFormTour = () => {
    setEditingSubject(null);
    setQueuedTour("form");
    setShowForm(true);
  };

  const handleView = (subject) => {
    setSelectedSubject(subject);
    setShowDetail(true);
  };

  const handleSeeAll = (statusKey) => {
    setFilters((prev) => ({ ...prev, status: statusKey }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleToggleStatus = async (id, newStatus) => {
    try {
      await toggleSubjectStatus(id, newStatus);
      showSuccessToast("Đã cập nhật trạng thái");
      fetchSubjects();
      setSelectedSubject((prev) => ({ ...prev, status: newStatus }));
    } catch (err) {
      showErrorToast("Không thể cập nhật trạng thái");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSubject(id);
      showSuccessToast("Đã xóa môn học");
      setShowDetail(false);
      fetchSubjects();
    } catch {
      showErrorToast("Không thể xóa môn học");
    }
  };

  // Logic phân nhóm dữ liệu
  const groupByStatus = (data = []) => {
    return {
      active: data.filter((s) => s.status === "active"),
      draft: data.filter((s) => s.status === "draft"),
      // Nhóm Đã đóng: lọc chính xác status 'inactive' 
      // hoặc các môn không nằm trong 2 nhóm trên
      closed: data.filter(
        (s) =>
          s.status === "inactive" ||
          !["active", "draft"].includes(s.status)
      ),
    };
  };

  // ===================== RENDER CARD =====================
  const renderCard = (s) => {
    const isActive = s.status === "active";
    const isDraft = s.status === "draft";

    return (
      <div
        key={s.id}
        onClick={() => handleView(s)}
        className="group relative bg-white rounded-xl border border-gray-100 shadow-sm 
                   hover:shadow-md transition-all duration-200 
                   cursor-pointer overflow-hidden flex flex-col h-full"
      >
        <div
          className={`h-1 w-full ${
            isActive ? "bg-red-500" : isDraft ? "bg-orange-400" : "bg-gray-400"
          }`}
        />

        <div className="p-4 flex-1">
          <div className="flex justify-between items-start mb-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform"
              style={{ backgroundColor: s.color || "#EF4444" }}
            >
              <FontAwesomeIcon
                icon={ICON_MAP[s.icon] || faBook}
                className="text-white text-base"
              />
            </div>
            <span
              className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider
              ${
                isActive
                  ? "bg-red-50 text-red-600 border border-red-100"
                  : isDraft
                  ? "bg-orange-50 text-orange-600 border border-orange-100"
                  : "bg-gray-50 text-gray-600"
              }`}
            >
              {isActive ? "Đang dạy" : isDraft ? "Nháp" : "Đã đóng"}
            </span>
          </div>

          <h3 className="font-bold text-gray-800 text-base mb-1 group-hover:text-red-600 transition-colors line-clamp-1">
            {s.name}
          </h3>

          <div className="flex items-center text-[11px] text-gray-400 mb-3 uppercase font-medium">
            <Code size={10} className="mr-1" /> {s.code}
          </div>

          <div className="space-y-1.5 mb-2">
            <div className="flex items-center text-xs text-gray-600">
              <Layers size={12} className="mr-2 text-gray-400" />
              <span>
                Khóa học:{" "}
                <span className="font-semibold text-gray-700">
                  {s.course_count || 0}
                </span>
              </span>
            </div>
          </div>
        </div>

        <div className="px-4 py-2 bg-gray-50/50 border-t border-gray-50 flex justify-between items-center">
          <span className="text-[10px] font-medium text-gray-400">
            STT: {s.sort_order ?? 0}
          </span>
          <div className="flex items-center text-[10px] font-bold text-red-600 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
            CHI TIẾT <ArrowRight size={12} className="ml-1" />
          </div>
        </div>
      </div>
    );
  };

  // GỌI HÀM PHÂN NHÓM Ở ĐÂY TRƯỚC KHI RETURN
  const statusGroups = groupByStatus(subjects);

  return (
    <div className="p-4 bg-[#F8F9FA] min-h-screen">

{/* HEADER */}
<div className="flex items-center justify-between mb-8" data-tour="subjects-header">
  <div className="flex items-center gap-4">
    {/* Icon Wrapper với Shadow Red */}
    <div className="bg-red-700 p-2.5 rounded-2xl text-white shadow-lg shadow-red-200">
      <FontAwesomeIcon icon={faGraduationCap} className="text-xl w-6 h-6 flex items-center justify-center" />
    </div>
    
    <div>
      {/* Title với style font-black và uppercase đồng bộ */}
      <h1 className="text-xl font-black text-gray-800 tracking-tight uppercase">
        Quản lý <span className="text-red-700">môn học</span>
      </h1>
      
      {/* Subtitle với hiệu ứng pulse và font tracking-widest */}
      <div className="flex items-center gap-2 mt-0.5">
        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
        <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider text-nowrap">
          <span className="text-red-600">{subjects.length}</span> môn học đang hiển thị
        </p>
      </div>
    </div>
  </div>
  {canQuickTour ? (
    <div className="flex items-center gap-2" data-tour="subjects-header-guides">
      <HeaderIconButton icon={Sparkles} label="Hướng dẫn nhanh" onClick={handlePageTour} />
      <HeaderIconButton icon={FilePenLine} label="Tour form" onClick={handleFormTour} />
      <HeaderIconButton icon={CircleHelp} label="Hướng dẫn sử dụng" onClick={openGuide} />
    </div>
  ) : null}
</div>


      {/* FILTER */}
      <div data-tour="subjects-filter">
        <SubjectFilter
          onNewClick={handleCreate}
          onFilterChange={setFilters}
          currentFilters={filters}
        />
      </div>

      {/* GRID CONTAINER */}
      <div className="space-y-10" data-tour="subjects-sections">
        {[
          {
            key: "active",
            title: "Môn học đang dạy",
            data: statusGroups.active,
            color: "text-red-600",
            bg: "bg-red-50",
            dot: "bg-red-500 animate-pulse",
          },
          {
            key: "draft",
            title: "Môn học mới (Nháp)",
            data: statusGroups.draft,
            color: "text-orange-600",
            bg: "bg-orange-50",
            dot: "bg-orange-400",
          },
          {
            key: "closed",
            title: "Môn học đã đóng",
            data: statusGroups.closed,
            color: "text-gray-600",
            bg: "bg-gray-100",
            dot: "bg-gray-500",
          },
        ]
          .filter(
            (section) => filters.status === "" || filters.status === section.key
          )
          .map(
            (section) =>
              section.data.length > 0 && (
                <div key={section.key}>
                  <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`p-1.5 rounded-lg ${section.bg} ${section.color}`}
                      >
                        <GraduationCap size={18} />
                      </div>
                      <div>
                        <h2
                          className={`text-sm font-bold tracking-wide uppercase ${section.color}`}
                        >
                          {section.title}
                        </h2>
                        <p className="text-[11px] text-gray-400 flex items-center gap-1.5 mt-0.5 font-medium">
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${section.dot}`}
                          />
                          Hiện có {section.data.length} nội dung trong danh sách
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        handleSeeAll(filters.status === "" ? section.key : "")
                      }
                      className={`text-[11px] font-bold py-1.5 px-3 rounded-lg transition-all border
                  ${
                    filters.status === ""
                      ? "text-red-600 border-red-100 hover:bg-red-600 hover:text-white"
                      : "text-white bg-gray-800 border-gray-800 hover:bg-black"
                  }`}
                    >
                      {filters.status === "" ? "Xem tất cả" : "Quay lại"}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {section.data.map((course) => renderCard(course))}
                  </div>
                </div>
              )
          )}
      </div>

      {/* MODALS */}
      {showForm && (
        <SubjectFormModal
          open={showForm}
          data={editingSubject}
          onClose={(reload) => {
            setShowForm(false);
            setQueuedTour(null);
            if (reload) fetchSubjects();
          }}
        />
      )}

      {selectedSubject && (
        <SubjectDetailModal
          isOpen={showDetail}
          subject={selectedSubject}
          onClose={() => setShowDetail(false)}
          onEdit={(subject) => {
            setEditingSubject(subject);
            setShowDetail(false);
            setShowForm(true);
          }}
          onDelete={handleDelete}
          onToggleStatus={handleToggleStatus}
        />
      )}
    </div>
  );
};

export default SubjectCardPage;

function HeaderIconButton({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300"
    >
      <Icon className="h-5 w-5" strokeWidth={2.2} />
      <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
        {label}
      </span>
    </button>
  );
}
